import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { HistoricalDataPoint } from "../../types/plan";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SERIES_COLORS = [
  "#0f3557",
  "#36A2EB",
  "#FF6384",
  "#4BC0C0",
  "#FFCE56",
  "#9966FF",
  "#FF9F40",
];

function parseHistoricalDate(s: string): Date | null {
  if (!s || typeof s !== "string") return null;
  const parts = s.trim().split(/[-/.]/);
  if (parts.length !== 3) return null;
  const a = Number(parts[0]);
  const b = Number(parts[1]);
  const c = Number(parts[2]);
  if (!a || !b || !c) return null;

  // ISO: YYYY-MM-DD (first part is a 4-digit year)
  if (parts[0].length === 4) {
    return new Date(a, b - 1, c);
  }
  // Trailing 4-digit year: either DMY or MDY
  if (parts[2].length === 4 || c > 31) {
    // Disambiguate: if first segment > 12 → DMY; if second segment > 12 → MDY;
    // otherwise default to MDY when the separator is "/" and DMY otherwise.
    if (a > 12) return new Date(c, b - 1, a); // DMY
    if (b > 12) return new Date(c, a - 1, b); // MDY
    const usesSlash = s.includes("/");
    return usesSlash
      ? new Date(c, a - 1, b) // MDY
      : new Date(c, b - 1, a); // DMY
  }
  return null;
}

function parseNumeric(v: any): number {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return 0;
  const cleaned = v.replace(/[^0-9.\-]/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

function isPercentageValue(v: any): boolean {
  return typeof v === "string" && v.includes("%");
}

function detectCurrencySymbol(
  rows: HistoricalDataPoint[],
  key: string
): string {
  // Extract any non-numeric prefix or suffix (currency symbol or 3-letter code
  // like THB, USD, CHF) from a value that isn't a percentage.
  for (const row of rows) {
    const v = row[key];
    if (typeof v !== "string" || v.includes("%")) continue;
    const trimmed = v.trim();
    const prefix = trimmed.match(/^[^\d\s.,\-]+/);
    if (prefix) return prefix[0];
    const suffix = trimmed.match(/[^\d\s.,\-]+$/);
    if (suffix) return suffix[0];
  }
  return "";
}

const DBA_KEY_PATTERNS = ["dba", "tenant", "occupant", "operator"];

function findDbaKey(metricKeys: string[]): string | null {
  // Prefer an exact-ish match before falling back to a contains match.
  for (const candidate of metricKeys) {
    const lc = candidate.toLowerCase();
    if (DBA_KEY_PATTERNS.includes(lc)) return candidate;
  }
  for (const candidate of metricKeys) {
    const lc = candidate.toLowerCase();
    if (DBA_KEY_PATTERNS.some((p) => lc.includes(p))) return candidate;
  }
  return null;
}

interface HistoricalDataViewProps {
  historicalData: HistoricalDataPoint[];
}

const HistoricalDataView: React.FC<HistoricalDataViewProps> = ({
  historicalData,
}) => {
  const metricKeys = useMemo(() => {
    const set = new Set<string>();
    historicalData.forEach((row) =>
      Object.keys(row).forEach((k) => {
        if (k !== "date") set.add(k);
      })
    );
    return Array.from(set);
  }, [historicalData]);

  const dbaKey = useMemo(() => findDbaKey(metricKeys), [metricKeys]);

  const percentageKeys = useMemo(
    () =>
      metricKeys.filter(
        (k) =>
          k !== dbaKey &&
          historicalData.some((r) => isPercentageValue(r[k]))
      ),
    [metricKeys, historicalData, dbaKey]
  );

  const amountKeys = useMemo(
    () =>
      metricKeys.filter(
        (k) => k !== dbaKey && !percentageKeys.includes(k)
      ),
    [metricKeys, percentageKeys, dbaKey]
  );

  const currencySymbol = useMemo(() => {
    for (const k of amountKeys) {
      const s = detectCurrencySymbol(historicalData, k);
      if (s) return s;
    }
    return "";
  }, [historicalData, amountKeys]);

  const chartRows = useMemo(
    () =>
      [...historicalData]
        .map((row) => ({ row, date: parseHistoricalDate(row.date) }))
        .filter((r) => r.date !== null)
        .sort((a, b) => a.date!.getTime() - b.date!.getTime()),
    [historicalData]
  );

  const tableRows = useMemo(
    () =>
      [...historicalData]
        .map((row) => ({ row, date: parseHistoricalDate(row.date) }))
        .sort((a, b) => {
          if (!a.date || !b.date) return 0;
          return b.date.getTime() - a.date.getTime();
        }),
    [historicalData]
  );

  // Detect DBA transitions in chronological order. Each transition is the
  // index in chartRows where the value differs from the previous row.
  const dbaTransitions = useMemo(() => {
    if (!dbaKey) return [] as Array<{
      index: number;
      from: string;
      to: string;
    }>;
    const out: Array<{ index: number; from: string; to: string }> = [];
    let prev: string | null = null;
    chartRows.forEach(({ row }, i) => {
      const v = row[dbaKey];
      const current = v === undefined || v === null ? "" : String(v);
      if (prev !== null && current && current !== prev) {
        out.push({ index: i, from: prev, to: current });
      }
      if (current) prev = current;
    });
    return out;
  }, [chartRows, dbaKey]);

  // For the table, mark cells where the DBA differs from the chronologically
  // previous row (the row immediately after it in the descending table).
  const dbaChangedRowIndexes = useMemo(() => {
    if (!dbaKey) return new Set<number>();
    const set = new Set<number>();
    for (let i = 0; i < tableRows.length - 1; i++) {
      const current = String(tableRows[i].row[dbaKey] ?? "");
      const previous = String(tableRows[i + 1].row[dbaKey] ?? "");
      if (current && previous && current !== previous) {
        set.add(i);
      }
    }
    return set;
  }, [tableRows, dbaKey]);

  const datasets = useMemo(() => {
    const ds: any[] = [];
    amountKeys.forEach((k, i) => {
      ds.push({
        label: k,
        data: chartRows.map(({ row }) => parseNumeric(row[k])),
        borderColor: SERIES_COLORS[i % SERIES_COLORS.length],
        backgroundColor: SERIES_COLORS[i % SERIES_COLORS.length] + "22",
        yAxisID: "y",
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
      });
    });
    percentageKeys.forEach((k, i) => {
      const colorIdx = (i + amountKeys.length) % SERIES_COLORS.length;
      ds.push({
        label: k,
        data: chartRows.map(({ row }) => parseNumeric(row[k])),
        borderColor: SERIES_COLORS[colorIdx],
        backgroundColor: SERIES_COLORS[colorIdx] + "22",
        yAxisID: "y1",
        tension: 0.3,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 2,
        pointHoverRadius: 4,
      });
    });
    return ds;
  }, [chartRows, amountKeys, percentageKeys]);

  const chartData = useMemo(
    () => ({
      labels: chartRows.map(({ row }) => row.date),
      datasets,
    }),
    [chartRows, datasets]
  );

  const dbaChangesPlugin = useMemo(
    () => ({
      id: "dbaChanges",
      afterDatasetsDraw: (chart: any) => {
        if (!dbaTransitions.length) return;
        const { ctx, chartArea, scales } = chart;
        if (!chartArea || !scales?.x) return;
        ctx.save();
        dbaTransitions.forEach(({ index, to }) => {
          const x = scales.x.getPixelForValue(index);
          if (Number.isNaN(x)) return;
          // Dashed vertical line
          ctx.strokeStyle = "#dc2626";
          ctx.lineWidth = 1.5;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
          // Label above the chart area
          ctx.setLineDash([]);
          ctx.fillStyle = "#dc2626";
          ctx.font = "bold 10px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(`→ ${to}`, x, chartArea.top - 2);
        });
        ctx.restore();
      },
    }),
    [dbaTransitions]
  );

  const chartOptions = useMemo(() => {
    const scales: any = {
      y: {
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: currencySymbol ? `Amount (${currencySymbol})` : "Amount",
        },
      },
    };
    if (percentageKeys.length > 0) {
      scales.y1 = {
        type: "linear",
        position: "right",
        title: { display: true, text: "Percentage (%)" },
        grid: { drawOnChartArea: false },
        ticks: {
          callback: (value: any) => `${value}%`,
        },
      };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      layout: dbaTransitions.length
        ? { padding: { top: 18 } }
        : undefined,
      plugins: {
        legend: { position: "top" as const, labels: { boxWidth: 14, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (ctx: any) => {
              const v = ctx.parsed.y;
              const label = ctx.dataset.label;
              if (percentageKeys.includes(label)) {
                return `${label}: ${Number(v).toFixed(2)}%`;
              }
              return `${label}: ${currencySymbol}${Number(v).toLocaleString(
                undefined,
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              )}`;
            },
            afterBody: (items: any[]) => {
              if (!dbaKey || !items?.length) return undefined as any;
              const idx = items[0].dataIndex;
              const row = chartRows[idx]?.row;
              if (!row) return undefined as any;
              const dbaVal = row[dbaKey];
              if (dbaVal === undefined || dbaVal === null || dbaVal === "")
                return undefined as any;
              return [`${dbaKey}: ${String(dbaVal)}`];
            },
          },
        },
      },
      scales,
    };
  }, [percentageKeys, currencySymbol, dbaTransitions.length, dbaKey, chartRows]);

  if (!historicalData || historicalData.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic p-4">
        No historical data available.
      </div>
    );
  }

  return (
    <div className="w-full px-2">
      <div
        className="mb-4 bg-white border border-gray-200 rounded-lg p-3"
        style={{ height: 320, position: "relative" }}
      >
        <Line
          data={chartData}
          options={chartOptions as any}
          plugins={[dbaChangesPlugin]}
        />
      </div>
      {dbaKey && dbaTransitions.length > 0 && (
        <div className="mb-3 flex items-center gap-2 text-[11px] text-gray-600">
          <span className="inline-block h-0 w-4 border-t-2 border-red-600 border-dashed" />
          <span>
            {dbaTransitions.length} {dbaKey} change
            {dbaTransitions.length > 1 ? "s" : ""} marked on the chart
          </span>
        </div>
      )}

      <div className="overflow-x-auto overflow-y-auto max-h-96 border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 text-xs">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap">
                Date
              </th>
              {metricKeys.map((k) => (
                <th
                  key={k}
                  className="px-3 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap"
                >
                  {k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableRows.map(({ row }, i) => {
              const dbaChanged = dbaChangedRowIndexes.has(i);
              return (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td
                    className="px-3 py-2 border-r border-gray-200 font-semibold whitespace-nowrap"
                    style={{ color: "#0f3557" }}
                  >
                    {row.date}
                  </td>
                  {metricKeys.map((k) => {
                    const isDbaCell = dbaKey === k;
                    const highlightCell = isDbaCell && dbaChanged;
                    return (
                      <td
                        key={k}
                        className={`px-3 py-2 border-r border-gray-200 whitespace-nowrap ${
                          highlightCell
                            ? "bg-red-50 text-red-700 font-semibold"
                            : "text-gray-700"
                        }`}
                        title={
                          highlightCell
                            ? `${k} changed from previous period`
                            : undefined
                        }
                      >
                        {row[k] !== undefined && row[k] !== null
                          ? String(row[k])
                          : ""}
                        {highlightCell && (
                          <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-red-600 text-white align-middle">
                            NEW
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoricalDataView;
